import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { CardButtonComponent } from 'src/app/components/ui-components/buttons/card-button/card-button.component';
import { SharedModule } from "../../../shared.module";
import { DbSchemeEnum } from 'src/app/enums/db-scheme.enum';
import { IExecuteProcedureRequest, IExecuteProcedureResult, IGetCashMovementsSummaryRequest, IGetCashMovementsSummaryResponse } from 'src/app/services/model/pos.api.model';
import { ProcedureNameEnum } from 'src/app/enums/procedure-name.enum';
import { BaseClosingComponent, Denomination } from '../base-closing.component';
import { HeaderProcessingService } from 'src/app/services/header-processing.service';
import { ReportExportService } from 'src/app/services/report-export.service';
import { ReportType } from 'src/app/services/model/reportType.model';
import { ReportUrlService } from 'src/app/services/report-url.service';
import { PopUpInformalType } from 'src/app/components/ui-components/pop-ups/pop-up-informal/pop-up-informal.model';
import { lastValueFrom, from, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'app-money-closing',
  templateUrl: './money-closing.component.html',
  styleUrls: ['./money-closing.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslateModule, ReactiveFormsModule, CardButtonComponent, SharedModule],
})
export class MoneyClosingComponent extends BaseClosingComponent implements OnInit, OnDestroy {
  banknotes: Denomination[] = [];
  coins: Denomination[] = [];

  private headerProcessingService = inject(HeaderProcessingService);
  private reportExportService = inject(ReportExportService);
  private reportUrlService = inject(ReportUrlService);

  public reportUrl: string | null = this.reportUrlService.getReportUrl(ReportType.cashInventory, { HeaderID: 0 });

  constructor(
  ) {
    super();
  }

  async ngOnInit() {
    // Get all payment types with nominals
    const paymentTypesWithNominals = await this.paymentTypesService.getPaymentTypesWithNominals();
    
    // Find cash payment type (TenderType = 0) for user's currency
    const cashPaymentType = paymentTypesWithNominals?.find(
      (pt: any) => pt.TenderType === 0 && pt.Currency === this.currencyCode
    );
    
    if (!cashPaymentType) {
      console.error(`No cash payment type found for currency: ${this.currencyCode}`);
      return;
    }

    // Get cash denominations for the payment type
    const cashDenominations = await this.paymentTypesService.getCashDenominations(cashPaymentType.Name);
    
    // Split denominations into banknotes and coins (assuming banknotes >= 100, coins < 100)
    // TODO: Can be different based on currency
    this.banknotes = cashDenominations
      .filter((d: any) => d.value >= 100)
      .map((d: any) => ({ value: d.value, count: 0, total: 0 }))
      .sort((a, b) => b.value - a.value);
    
    this.coins = cashDenominations
      .filter((d: any) => d.value < 100)
      .map((d: any) => ({ value: d.value, count: 0, total: 0 }))
      .sort((a, b) => b.value - a.value);

    // Populate the base class denominations array with all denominations
    this.denominations = [...this.banknotes, ...this.coins];

    // Create form controls for denominations
    const formControls: any = {};
    this.denominations.forEach((d) => { formControls[`denomination_${d.value}`] = 0; });

    // Add denomination controls to existing form (which already has accountingState, confirm, print)
    Object.keys(formControls).forEach(key => {
      this.closingForm.addControl(key, this.fb.control(formControls[key]));
    });

    // Add note control for when there's a difference
    this.closingForm.addControl('note', this.fb.control(''));

    // Subscribe to form changes after form is fully initialized
    this.subscribeToFormChanges();

    // Initial calculation after form is set up
    this.calculateTotals();

    // Call stored procedure to get cash movements summary
    await this.getCashMovementsSummary();
  }

  private async getCashMovementsSummary() {
    const cashPaymentType = await this._sharedService.getCashPaymentTypeForCurrency();

    if (!cashPaymentType) {
      console.error(`No cash payment type found for currency: ${this.currencyCode}`);
      return;
    }

    const request: IExecuteProcedureRequest<IGetCashMovementsSummaryRequest> = {
      DbScheme: DbSchemeEnum.Dert,
      ProcedureName: ProcedureNameEnum.GetCashMovementsSummary,
      Parameters: {
        CurrencyCode: this.currencyCode,
        ReturnToResult: 0,
        PaymentName: cashPaymentType.Name,
      } as IGetCashMovementsSummaryRequest,
    };

    const result = await lastValueFrom(
      from(this.sharedApiService.executeProcedure<IGetCashMovementsSummaryRequest, IExecuteProcedureResult<IGetCashMovementsSummaryResponse>>(request)).pipe(
        catchError(error => { console.error('Error getting cash movements summary', error); return of(null); })
      )
    );

    if (result?.data?.TotalMovement !== undefined && result.data.TotalMovement !== null) {
      this.totalRegisteredValue = result.data.TotalMovement;
      this.closingForm.patchValue({ totalRegisteredValue: this.totalRegisteredValue });
      this.calculateTotals();
    }
  }


  calculateTotals() {
    if (!this.closingForm) {
      return;
    }

    let total = 0;
    this.denominations.forEach((d, i) => {
      const count = this.getControlValue(`denomination_${d.value}`);
      d.count = count;
      d.total = d.value * count;
      total += d.total;
    });

    this.totalValue = total;
    
    this.calculateDifference(this.totalValue, this.totalRegisteredValue);

    // Set note control as required/optional based on difference
    const noteControl = this.closingForm.get('note');
    if (noteControl) {
      if (this.difference !== 0) {
        noteControl.setValidators([Validators.required]);
      } else {
        noteControl.clearValidators();
      }
      noteControl.updateValueAndValidity();
    }
  }

  async confirm(isMoneyClosing: boolean) {
    const res = await super.confirm(isMoneyClosing);

    if (res && res.success && isMoneyClosing) {
      // Show success dialog with print option
      const dialogResult = await this.popUpDialogService.confirm({
        title: 'webPos.Done',
        message: 'webPos.Closings.CashCheckCreatedSuccessfully',
        type: PopUpInformalType.Success,
        showPrintReceipt: true,
        showSendReceiptByEmail: true,
        emailAddress: '',
      });

      if (dialogResult?.printReceipt && res.trxUID) {
        const headerProcessingResult = await lastValueFrom(
          from(this.headerProcessingService.monitorHeader(res.trxUID)).pipe(
            catchError(error => { console.error('Header processing failed', error); return of(null); })
          )
        );

        const headerIdForPrint = headerProcessingResult
          ? this.headerProcessingService.getHeaderIdFromResult(headerProcessingResult)
          : null;

        if (headerIdForPrint) {
          await lastValueFrom(
            from(this.reportExportService.downloadPdf(
              undefined,
              [{ key: 'HeaderID', value: headerIdForPrint.toString() }],
              true
            )).pipe(
              catchError(error => { console.error('PDF download failed', error); return of(null); })
            )
          );

          if (dialogResult.email) {
            await lastValueFrom(
              from(this.reportExportService.sendPdfByEmail(dialogResult.email, headerIdForPrint)).pipe(
                catchError(error => { console.error('Email sending failed', error); return of(null); })
              )
            );
          }

          console.log('Money closing report is ready to print.');
        }
      }

      this.router.navigate(['/dashboard']);
    }
    
    return res;
  }
}
